import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  DashboardOutlined,
  TeamOutlined,
  CloudServerOutlined,
  CreditCardOutlined,
  AuditOutlined,
  ArrowLeftOutlined,
  AppstoreOutlined,
  DollarOutlined,
  MailOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined />, path: '/admin' },
  { id: 'clients',   label: 'Clientes',  icon: <TeamOutlined />,        path: '/admin/clients' },
  { id: 'services',  label: 'Servicios', icon: <CloudServerOutlined />, path: '/admin/services' },
  { id: 'payments',  label: 'Pagos',     icon: <CreditCardOutlined />,  path: '/admin/payments' },
  { id: 'products',  label: 'Productos', icon: <AppstoreOutlined />,    path: '/admin/products' },
  { id: 'pricing',   label: 'Precios',   icon: <DollarOutlined />,      path: '/admin/pricing' },
  { id: 'notifications', label: 'Notificaciones', icon: <MailOutlined />, path: '/admin/notifications' },
  { id: 'audit',     label: 'Auditoría', icon: <AuditOutlined />,       path: '/admin/audit' },
  { id: 'settings',  label: 'Configuración', icon: <SettingOutlined />, path: '/admin/settings' },
];

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { logout, user } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const currentPath = location.pathname;
  const isActive = (path: string) =>
    path === '/admin' ? currentPath === '/admin' : currentPath.startsWith(path);

  const canGoBack = currentPath !== '/admin';

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-screen bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800
          transition-all duration-300 z-40
          ${sidebarOpen ? 'w-64' : 'w-16'}
        `}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-800">
          <div className={`flex items-center gap-2 ${!sidebarOpen && 'justify-center w-full'}`}>
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              K
            </div>
            {sidebarOpen && (
              <span className="font-bold text-gray-900 dark:text-white text-sm">
                Kubiy Admin
              </span>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="px-2 py-4 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-150
                ${isActive(item.path)
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }
              `}
              title={sidebarOpen ? '' : item.label}
            >
              <span className="text-base flex-shrink-0">{item.icon}</span>
              {sidebarOpen && <span className="text-sm">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 px-2 py-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={logout}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20
              transition-colors duration-150
            `}
            title={sidebarOpen ? '' : 'Cerrar sesión'}
          >
            <LogoutOutlined className="flex-shrink-0" />
            {sidebarOpen && <span className="text-sm">Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        {/* Top Bar */}
        <header className="h-14 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
          <div className="flex items-center justify-between h-full px-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {sidebarOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
              </button>

              {canGoBack && (
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-white transition-colors"
                >
                  <ArrowLeftOutlined />
                  <span>Volver</span>
                </button>
              )}
            </div>

            {/* User */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-300 hidden sm:block">
                {user?.name || user?.email}
              </span>
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full flex items-center justify-center text-white text-xs font-bold">
                <UserOutlined />
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
